import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';

const WHATSAPP_NUMBER = '918758529995';
const DEFAULT_MESSAGE = 'Hi NOVA! I want to know more about your jewelry';

const WhatsAppButton = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  // Hide on admin panel
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (dismissed || isAdmin) return;
    const timer = setTimeout(() => setShowTooltip(true), 3500);
    const hideTimer = setTimeout(() => setShowTooltip(false), 12000);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, [dismissed, isAdmin]);

  if (isAdmin) return null;

  const handleClick = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const dismissTooltip = (e) => {
    e.stopPropagation();
    setShowTooltip(false);
    setDismissed(true);
  };

  return (
    <div
      data-testid="whatsapp-floating-wrapper"
      className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-[60] flex flex-col items-end gap-3 pointer-events-none"
    >
      {/* Tooltip / Welcome bubble */}
      {showTooltip && (
        <div
          data-testid="whatsapp-tooltip"
          className="pointer-events-auto relative bg-[#FDFBF7] border border-[#2D3B26]/10 shadow-2xl rounded-2xl px-4 py-3 pr-9 max-w-[260px] animate-in fade-in slide-in-from-bottom-4 duration-500"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          <button
            type="button"
            onClick={dismissTooltip}
            data-testid="whatsapp-tooltip-close"
            className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-[#2D3B26]/5 text-[#7A8A73]"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <p className="text-[13px] font-medium text-[#2D3B26] leading-snug">
            Hi! Need help choosing the perfect piece? <span className="text-[#C27054]">Chat with us</span> on WhatsApp.
          </p>
          <div className="absolute -bottom-1.5 right-7 w-3 h-3 bg-[#FDFBF7] border-r border-b border-[#2D3B26]/10 rotate-45"></div>
        </div>
      )}

      {/* Floating Button */}
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        data-testid="whatsapp-floating-button"
        aria-label="Chat on WhatsApp"
        className="pointer-events-auto group relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1DA851] shadow-[0_8px_24px_rgba(37,211,102,0.45)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.6)] transition-all duration-300 hover:scale-110 active:scale-95"
      >
        {/* Pulse rings */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping"></span>
        <span className="absolute inset-0 rounded-full bg-[#25D366]"></span>

        {/* WhatsApp Icon (official glyph) */}
        <svg
          viewBox="0 0 32 32"
          className="relative w-7 h-7 fill-white"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.39.13-.358.13-.66.085-.99-.058-.115-.244-.172-.515-.301-.272-.143-1.674-.823-1.933-.823zM16.005 2.067c-7.8 0-14.166 6.34-14.166 14.142 0 2.51.658 4.937 1.92 7.07L1 31l7.913-2.073c2.05 1.116 4.36 1.706 6.706 1.706h.005c7.8 0 14.179-6.34 14.179-14.142s-6.378-14.142-14.18-14.142zm0 25.876h-.005c-2.092 0-4.144-.567-5.929-1.625l-.428-.252-4.402 1.155 1.176-4.293-.275-.444a11.741 11.741 0 0 1-1.844-6.273c0-6.485 5.286-11.764 11.787-11.764 3.156 0 6.116 1.226 8.336 3.45a11.722 11.722 0 0 1 3.45 8.328c0 6.485-5.288 11.764-11.787 11.764z" />
        </svg>
      </button>
    </div>
  );
};

export default WhatsAppButton;
